import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { startWith } from 'rxjs';

import { TimeService } from '@core/common-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import {
  ConfirmPositionChangeDialogData,
  ConfirmPositionChangeDialogResult,
  ConfirmPositionChangeMember,
} from '@core/tellent-positions-types';
import { DatePickerComponent } from '@core/time-ui';
import {
  AvatarComponent,
  ButtonComponent,
  CheckboxComponent,
  ConnectedOverlayDirective,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DividerComponent,
  DropdownComponent,
  DropdownHeaderComponent,
  IconComponent,
  InputComponent,
  OverlayOriginDirective,
} from '@core/user-interface-legacy';

@Component({
  selector: 'tlc-confirm-position-change-dialog',
  templateUrl: './confirm-position-change-dialog.component.html',
  styleUrls: ['./confirm-position-change-dialog.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    AvatarComponent,
    ButtonComponent,
    CheckboxComponent,
    ConnectedOverlayDirective,
    DatePickerComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    DividerComponent,
    DropdownComponent,
    DropdownHeaderComponent,
    IconComponent,
    InfoboxComponent,
    InputComponent,
    OverlayOriginDirective,
    TranslatePipe,
  ],
})
export class ConfirmPositionChangeDialogComponent implements DialogComponentI<
  ConfirmPositionChangeDialogData,
  ConfirmPositionChangeDialogResult
> {
  private readonly dialogRef = inject(DialogRef);
  private readonly timeService = inject(TimeService);

  protected readonly members = signal<ConfirmPositionChangeMember[]>([]);
  protected readonly targetPositionName = signal<string>('');
  protected readonly addToHistory = signal<boolean>(true);

  protected readonly effectiveDateControl = new FormControl<string>(
    this.timeService.format({ pattern: 'YYYY-MM-DD' }),
    { nonNullable: true },
  );

  protected readonly effectiveDate = toSignal(
    this.effectiveDateControl.valueChanges.pipe(startWith(this.effectiveDateControl.value)),
    { initialValue: this.effectiveDateControl.value },
  );

  protected readonly hasAnyCurrentPosition = computed(() =>
    this.members().some(member => member.currentPosition !== null),
  );

  protected readonly singleCurrentPositionName = computed(() => {
    const members = this.members();
    return members.length === 1 ? (members[0].currentPosition?.name ?? null) : null;
  });

  protected readonly isCorrection = computed(
    () => this.hasAnyCurrentPosition() && !this.addToHistory(),
  );

  protected readonly isFutureDate = computed(() =>
    this.timeService.isAfterToday(this.effectiveDate()),
  );

  protected readonly effectiveDateLabel = computed(() => {
    const date = this.effectiveDate();
    return this.timeService.isToday(date)
      ? null
      : this.timeService.format({ input: date, pattern: 'D MMM YYYY' });
  });

  protected readonly showFutureInfobox = computed(
    () => !this.isCorrection() && this.isFutureDate(),
  );

  public setDialogData(data: ConfirmPositionChangeDialogData): void {
    this.targetPositionName.set(data.targetPositionName);
    this.members.set(data.members);
  }

  public cancel(): void {
    this.dialogRef.close({ confirmed: false, addToHistory: false, effectiveDate: '' });
  }

  public confirm(): void {
    this.dialogRef.close({
      confirmed: true,
      addToHistory: this.addToHistory(),
      effectiveDate: this.effectiveDateControl.value,
    });
  }

  protected toggleHistory(checked: boolean): void {
    this.addToHistory.set(checked);
  }
}
